import { useCallback, useEffect, useState } from 'react';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import XButton from 'src/components/Buttons/XButton';
import TimedProgressBar from 'src/components/TimedProgresBar';
import AlertNotificationAction from 'src/components/Toasts/AlertNotifications/AlertNotificationAction';
import MinorAlertNotificationsPagination from 'src/components/Toasts/AlertNotifications/MinorAlertNotifications/MinorAlertNotificationsPagination';
import useNotificationStore from 'src/stores/useNotificationStore';
import { NotificationData } from 'src/utils/notifications';
import AlertToast from '../../AlertToast';

const NOTIFICATION_DURATION_MILLIS = 10000;

interface Props {
	notifications: NotificationData[];
	onDismiss: (id: string) => void;
}

export default function MinorAlertNotifications({
	notifications,
	onDismiss,
}: Props) {
	const { removeNotificationById, clearAllNotifications } =
		useNotificationStore();

	const [visibleNotificationIndex, setVisibleNotificationIndex] = useState(0);
	const visibleNotification = notifications[visibleNotificationIndex];

	const { message, description, action, id, disableAlertAutoDismiss } =
		visibleNotification;

	// An action with no label would render as a bare arrow, so treat it as absent.
	const hasAction = !!action && 'label' in action && !!action.label;

	const dismissCurrentNotification = useCallback(() => {
		if (id) {
			removeNotificationById(id);
			onDismiss(id);
		}
	}, [id, removeNotificationById, onDismiss]);

	// Using a synced ref here because `notifications` itself updates too often
	// during render, causing this fn to get recreated and in turn the interval
	// to get cleared and restarted multiple times, throwing off the timing.
	const syncedNotificationsRef = useSyncedRef(notifications);
	const dismissAllMinorNotifications = useCallback(() => {
		clearAllNotifications((n) => 'alertType' in n && n.alertType === 'minor');
		syncedNotificationsRef.current.forEach((n) => {
			if (n.id) {
				onDismiss(n.id);
			}
		});
	}, [clearAllNotifications, syncedNotificationsRef, onDismiss]);

	const isLastNotification =
		notifications.length > 1 &&
		visibleNotificationIndex === notifications.length - 1;

	// Can't use `useInterval` here because we need the interval to clear and
	// restart whenever the current visibile notification changes, which can
	// happen at any time due to pagination.
	useEffect(() => {
		if (disableAlertAutoDismiss) {
			return;
		}

		const interval = setInterval(() => {
			// If this is the last notification of multiple, dismiss all notifications.
			if (isLastNotification) {
				dismissAllMinorNotifications();
				return;
			}

			// Otherwise, we just dismiss the one we're on.
			dismissCurrentNotification();
		}, NOTIFICATION_DURATION_MILLIS);

		return () => clearInterval(interval);
	}, [
		isLastNotification,
		disableAlertAutoDismiss,
		dismissAllMinorNotifications,
		dismissCurrentNotification,
	]);

	const handleBack = () => {
		setVisibleNotificationIndex((prev) => prev - 1);
	};

	const handleForward = () => {
		setVisibleNotificationIndex((prev) => prev + 1);
	};

	return (
		<AlertToast>
			<div className="flex w-full grow flex-col gap-y-md p-lg">
				<div className="flex items-start justify-between gap-x-sm">
					<span className="typo-label-sm">{message}</span>
					{/** If the user closes any notification, we dismiss all of them. */}
					<XButton
						onClick={dismissAllMinorNotifications}
						// `-right-0.5` better aligns the icon edge with the content edge.
						className="relative -right-0.5"
					/>
				</div>
				{description && <span className="typo-body-xs">{description}</span>}
				{(notifications.length > 1 || hasAction) && (
					<div className="flex items-center justify-between">
						{notifications.length > 1 && (
							<MinorAlertNotificationsPagination
								visibleNotificationIndex={visibleNotificationIndex}
								notifications={notifications}
								handleBack={handleBack}
								handleForward={handleForward}
							/>
						)}
						{hasAction && (
							<AlertNotificationAction
								alertType="minor"
								action={action}
								className="ml-auto"
								dismissNotification={
									isLastNotification
										? dismissAllMinorNotifications
										: dismissCurrentNotification
								}
							>
								{action.label}
							</AlertNotificationAction>
						)}
					</div>
				)}
			</div>
			{!disableAlertAutoDismiss && (
				<TimedProgressBar
					durationMillis={NOTIFICATION_DURATION_MILLIS}
					key={id}
				/>
			)}
		</AlertToast>
	);
}
